import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/**
 * The warehouse this session is working in.
 *
 * **Why one at a time, globally.** Permissions resolve per warehouse — the same person can be a
 * picker for one 3PL client and read-only for another — so the navigation menu has no answer
 * until something says which warehouse. Screens used to each carry their own picker, which cannot
 * answer it: if Inventory says Dallas while Locations says Reno, neither one built the menu.
 *
 * Legacy reached the same shape. It keeps the selected site in the header with a button to change
 * it (`_Layout.cshtml`, the `fa-sitemap` icon) and resolves permissions for that site alone.
 *
 * **Read by the API client on every request**, as `X-Warehouse-Id`. That is why the value lives
 * in a module-level store rather than React context: `apiRequest` is a plain function called from
 * query functions, and threading a warehouse through every one of them is the kind of change that
 * gets forgotten on the fortieth call site. Forgetting is not silent — warehouse-scoped endpoints
 * answer 403 rather than guessing — but a store nobody has to remember is better still.
 *
 * **Persisted**, so a reload or a fresh app launch resumes where somebody was working rather than
 * dropping them somewhere else. It is only a preference: the server re-derives what the user may
 * reach on every request, so a stored id they have since lost access to earns a 403, and
 * WarehouseGate clears it at the next opportunity.
 *
 * **Persisted PER USER**, which is the part that had to be added. Signing out clears it, but a
 * session can end without one — an expired cookie, a closed tab, a handheld handed to the next
 * shift — and then the stored warehouse outlived the person who chose it. `claimFor` below
 * discards it when a different user signs in.
 */

interface CurrentWarehouseState {
  /** Null before a warehouse is chosen, and for anyone who can reach none. */
  warehouseId: string | null;
  warehouseCode: string | null;
  /**
   * Whose warehouse this is. Null in a store written before this field existed, which is treated
   * as "nobody's" — one unnecessary reset on upgrade, and the gate picks a warehouse straight
   * back. Preferring that to trusting an unowned value.
   */
  userId: string | null;
  setWarehouse: (warehouse: { warehouseId: string; warehouseCode: string } | null) => void;
  claimFor: (userId: string) => void;
}

export const useCurrentWarehouseStore = create<CurrentWarehouseState>()(
  persist(
    (set, get) => ({
      warehouseId: null,
      warehouseCode: null,
      userId: null,
      setWarehouse: (warehouse) =>
        set({
          warehouseId: warehouse?.warehouseId ?? null,
          // The code is kept beside the id purely so the header can render immediately on a cold
          // start, before the warehouse list has come back. Without it the header flashes blank
          // on every launch, which reads as "no warehouse selected" — the one state that has to
          // be unambiguous.
          warehouseCode: warehouse?.warehouseCode ?? null,
        }),
      claimFor: (userId) => {
        if (get().userId === userId) return;
        // Somebody else's warehouse, or nobody's. Cleared, not kept: reachability alone does not
        // catch this, because two people who both work in Dallas and Reno can reach each other's
        // stored warehouse, and the second one would silently start their shift in the first
        // one's building. On the 3PL tenant that is the wrong end client.
        set({ warehouseId: null, warehouseCode: null, userId });
      },
    }),
    { name: 'invetrak.currentWarehouse' },
  ),
);

/**
 * Records that the stored warehouse belongs to this user, discarding one stored by anybody else.
 *
 * Called from the `/me` query as soon as the session is known — deliberately there rather than in
 * an effect. `usePermissions` is gated on `/me` resolving, so this runs before the first
 * warehouse-scoped request goes out; from an effect it would run one render too late, and that
 * render is the one that asks the API for permissions in the previous user's warehouse.
 */
export function claimWarehouseFor(userId: string): void {
  useCurrentWarehouseStore.getState().claimFor(userId);
}

/**
 * The current warehouse id, for the API client. Deliberately not a hook: this is read from
 * `apiRequest`, which is not a component.
 */
export function readCurrentWarehouseId(): string | null {
  return useCurrentWarehouseStore.getState().warehouseId;
}

/**
 * The current warehouse id, for a screen inside the app shell where one is guaranteed.
 *
 * The shell does not render its routes until a warehouse is chosen — see WarehouseGate — so a
 * screen can rely on this. It still returns a string rather than throwing on null, because a
 * screen that throws during the one render before the gate settles is a white page for a state
 * that resolves on its own.
 */
export function useCurrentWarehouseId(): string {
  return useCurrentWarehouseStore((s) => s.warehouseId) ?? '';
}
